import React, { useEffect, useState, useCallback, useMemo } from "react";
import { MobileJobCards } from "@/components/MobileJobCards";
import { ClientNotify } from "@/components/ClientNotify";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { useUI } from "@/context/UIContext";
import { useAuth } from "@/context/AuthContext";
import { Card, Badge, Btn, Input, Textarea, Select, Field, Modal } from "@/components/ui-kit";
import QRLabel from "@/components/QRLabel";
import { PendingMedia, uploadQueued } from "@/components/MediaUploader";
import {
  JOB_STATUSES, PRIORITIES, REPAIR_TYPES, findMeta, fmtDate, warrantyStatus,
} from "@/lib/constants";
import { PackagePlus, QrCode, CheckCircle2, Boxes, Layers, Tags, AlertTriangle } from "lucide-react";

export const WarrantyAlert = ({ job, compact: small }) => {
  const w = warrantyStatus(job);
  if (!w?.expired) return null;
  return (
    <span data-testid={`warranty-expired-${job.id || "form"}`} title={`Warranty (${w.months}) ended ${fmtDate(w.expiry)} · ${w.daysOver} days ago${w.from ? ` · issued on ${w.from}` : ""}`}
      className={`inline-flex items-center gap-1 rounded border border-red-500/40 bg-red-500/10 font-mono font-bold uppercase text-red-500 ${small ? "px-1 text-[9px]" : "px-1.5 py-0.5 text-[10px]"}`}>
      <AlertTriangle size={small ? 10 : 12} /> Out of warranty{small ? "" : ` · ${w.daysOver}d`}
    </span>
  );
};

const CHECKLIST = ["powers_on", "visible_damage", "accessories_complete", "corrosion", "liquid_damage"];
const CHECK_LABELS = {
  powers_on: "Powers On", visible_damage: "Visible Damage", accessories_complete: "Accessories Complete",
  corrosion: "Corrosion Present", liquid_damage: "Liquid Damage",
};

const typeLabel = (t) => findMeta(REPAIR_TYPES, t || "new_repair").label;

export const jobLabelProps = (j) => ({
  title: j.equipment_name, code: j.job_number, badge: typeLabel(j.repair_type),
  lines: [["SERIAL", j.serial_number], ["MODEL", j.model_number || "—"],
    j.batch_number ? ["BATCH", `${j.batch_number} · ${j.batch_index}/${j.batch_quantity}`] : ["RMA", j.tracking_rma || "—"],
    ["PRIORITY", j.priority]],
});

export default function Inward() {
  const { compact } = useUI();
  const { can } = useAuth();
  const canCreate = can("job.create");
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [locations, setLocations] = useState([]);
  const [techs, setTechs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [grouped, setGrouped] = useState(true);
  const [label, setLabel] = useState(null);
  const q = params.get("q") || "";

  const load = useCallback(() => {
    const p = {};
    if (q) p.q = q;
    if (statusFilter) p.status = statusFilter;
    if (typeFilter) p.repair_type = typeFilter;
    api.get("/jobs", { params: p }).then((r) => setJobs(r.data)).catch(() => {});
  }, [q, statusFilter, typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/locations").then((r) => setLocations(r.data));
    api.get("/users").then((r) => setTechs(r.data.filter((u) => u.role === "technician")));
    api.get("/projects").then((r) => setProjects(r.data));
  }, []);
  useEffect(() => { if (params.get("new")) { setOpen(true); params.delete("new"); setParams(params, { replace: true }); } }, [params, setParams]);

  const quickStatus = async (job, status) => {
    try {
      await api.put(`/jobs/${job.id}`, { status });
      toast.success(`${job.job_number} → ${findMeta(JOB_STATUSES, status).label}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  // Group batch jobs into one row when grouping is on
  const rows = useMemo(() => {
    if (!grouped) return jobs.map((j) => ({ kind: "job", job: j }));
    const out = []; const seen = {};
    for (const j of jobs) {
      if (!j.batch_id) { out.push({ kind: "job", job: j }); continue; }
      if (!seen[j.batch_id]) { seen[j.batch_id] = { kind: "batch", job: j, items: [] }; out.push(seen[j.batch_id]); }
      seen[j.batch_id].items.push(j);
    }
    return out;
  }, [jobs, grouped]);

  const showBatchLabel = (items) => {
    const j = items[0];
    setLabel({
      heading: `Batch Label · ${j.batch_number}`, title: j.equipment_name, code: j.batch_number, badge: typeLabel(j.repair_type),
      lines: [["QTY", `${j.batch_quantity} units`], ["MODEL", j.model_number || "—"], ["BRAND", j.brand || "—"], ["PRIORITY", j.priority]],
    });
  };
  const showUnitLabels = (items) => setLabel({
    heading: `Per-Unit Labels · ${items[0].batch_number}`,
    labels: [...items].sort((a, b) => a.batch_index - b.batch_index).map(jobLabelProps),
  });

  const rowPad = compact ? "py-1.5 text-xs" : "py-2.5 text-sm";

  return (
    <div className="space-y-4" data-testid="inward-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight sm:text-3xl">Inward · Job Cards</h1>
          <p className="text-sm text-muted-foreground">{jobs.length} equipment records {q && <>· filtered by “{q}”</>}</p>
        </div>
        <div className="grid w-full grid-cols-2 items-center gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="w-full sm:w-40" data-testid="job-type-filter">
            <option value="">All Types</option>
            {REPAIR_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="w-full sm:w-44" data-testid="job-status-filter">
            <option value="">All Statuses</option>
            {JOB_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </Select>
          <Btn variant="outline" onClick={() => setGrouped(!grouped)} data-testid="toggle-group-batches" title="Group batch units">
            <Layers size={15} /> {grouped ? "Grouped" : "Flat"}
          </Btn>
          {canCreate && <Btn onClick={() => setOpen(true)} data-testid="new-inward-btn"><PackagePlus size={16} /> New Inward</Btn>}
        </div>
      </div>

      <MobileJobCards rows={rows} onOpen={(j) => navigate(`/jobs/${j.id}`)} onLabel={(j) => setLabel(jobLabelProps(j))}
        onBatchLabel={showBatchLabel} onUnitLabels={showUnitLabels} onExpand={() => setGrouped(false)} />
      <Card className="hidden overflow-x-auto md:block">
        <table className="w-full min-w-[900px]">
          <thead>
            <tr className="border-b border-border text-left font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
              <th className="px-3 py-2">Job # / Batch</th>
              <th className="px-3 py-2">Equipment</th>
              <th className="px-3 py-2">Serial</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Priority</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Received</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const j = r.job;
              const pr = findMeta(PRIORITIES, j.priority);
              const rt = findMeta(REPAIR_TYPES, j.repair_type || "new_repair");
              if (r.kind === "batch") {
                const counts = r.items.reduce((a, x) => { a[x.status] = (a[x.status] || 0) + 1; return a; }, {});
                return (
                  <tr key={j.batch_id} className={`border-b border-border/60 bg-accent/20 hover:bg-accent/50 ${rowPad}`} data-testid={`batch-row-${j.batch_id}`}>
                    <td className="px-3 font-mono text-xs text-primary">
                      <div className="flex items-center gap-1"><Layers size={12} /> {j.batch_number}</div>
                      <div className="text-[10px] text-muted-foreground">{r.items.length} units</div>
                    </td>
                    <td className="px-3 font-medium">{j.equipment_name}<div className="font-mono text-[10px] text-muted-foreground">{j.brand} {j.model_number}</div></td>
                    <td className="px-3 font-mono text-[10px] text-muted-foreground">{r.items[0].serial_number} …</td>
                    <td className="px-3"><div className="flex flex-col items-start gap-0.5"><Badge className={rt.cls}>{rt.label}</Badge><WarrantyAlert job={j} compact /></div></td>
                    <td className="px-3"><span className={`font-mono text-xs font-bold ${pr.cls}`}>{pr.label}</span></td>
                    <td className="px-3">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(counts).map(([s, n]) => { const st = findMeta(JOB_STATUSES, s); return <Badge key={s} className={st.cls}>{n} {st.label}</Badge>; })}
                      </div>
                    </td>
                    <td className="px-3 font-mono text-[11px] text-muted-foreground">{fmtDate(j.received_at)}</td>
                    <td className="px-3">
                      <div className="flex items-center justify-end gap-1">
                        <button title="Batch label (one for all)" data-testid={`batch-qr-${j.batch_id}`} onClick={() => showBatchLabel(r.items)} className="rounded border border-border p-1 hover:bg-accent"><QrCode size={14} /></button>
                        <button title="Per-unit labels" data-testid={`batch-unit-labels-${j.batch_id}`} onClick={() => showUnitLabels(r.items)} className="rounded border border-border p-1 hover:bg-accent"><Tags size={14} /></button>
                        <button title="Show units" data-testid={`batch-expand-${j.batch_id}`} onClick={() => setGrouped(false)} className="rounded border border-border p-1 hover:bg-accent"><Boxes size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              }
              const st = findMeta(JOB_STATUSES, j.status);
              return (
                <tr key={j.id} className={`border-b border-border/60 hover:bg-accent/50 ${rowPad}`} data-testid={`job-row-${j.id}`}>
                  <td className="cursor-pointer px-3 font-mono text-xs text-primary" onClick={() => navigate(`/jobs/${j.id}`)}>
                    {j.job_number}
                    {j.batch_number && <div className="text-[10px] text-muted-foreground">{j.batch_number} · {j.batch_index}/{j.batch_quantity}</div>}
                  </td>
                  <td className="px-3 font-medium">{j.equipment_name}<div className="font-mono text-[10px] text-muted-foreground">{j.brand} {j.model_number}</div></td>
                  <td className="px-3 font-mono text-xs">{j.serial_number}</td>
                  <td className="px-3"><div className="flex flex-col items-start gap-0.5"><Badge className={rt.cls}>{rt.label}</Badge><WarrantyAlert job={j} compact /></div></td>
                  <td className="px-3"><span className={`font-mono text-xs font-bold ${pr.cls}`}>{pr.label}</span></td>
                  <td className="px-3"><Badge className={st.cls}>{st.label}</Badge></td>
                  <td className="px-3 font-mono text-[11px] text-muted-foreground">{fmtDate(j.received_at)}</td>
                  <td className="px-3">
                    <div className="flex items-center justify-end gap-1">
                      <button title="QR Label" data-testid={`qr-${j.id}`} onClick={() => setLabel(jobLabelProps(j))} className="rounded border border-border p-1 hover:bg-accent"><QrCode size={14} /></button>
                      <button title="Open workstation" data-testid={`open-${j.id}`} onClick={() => navigate(`/jobs/${j.id}`)} className="rounded border border-border p-1 hover:bg-accent"><Boxes size={14} /></button>
                      {j.status !== "dispatched" && j.status !== "qa_review" && (
                        <button title="Mark QA Review" data-testid={`qa-quick-${j.id}`} onClick={() => quickStatus(j, "qa_review")} className="rounded border border-border p-1 hover:bg-accent"><CheckCircle2 size={14} className="text-purple-500" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {rows.length === 0 && <tr><td colSpan={8} className="px-3 py-10 text-center text-sm text-muted-foreground">No job cards found.</td></tr>}
          </tbody>
        </table>
      </Card>

      {open && <InwardForm onClose={() => setOpen(false)} onSaved={(lbl) => { setOpen(false); load(); api.get("/locations").then((r) => setLocations(r.data)); if (lbl) setLabel(lbl); }} locations={locations} techs={techs} projects={projects} />}

      {label && <QRLabel open onClose={() => setLabel(null)} {...label} />}
    </div>
  );
}

function WarrantyFormAlert({ f, lookup }) {
  if (lookup === "loading") return <div className="text-xs text-muted-foreground sm:col-span-2">Checking warranty history for this serial…</div>;
  const w = warrantyStatus({ ...f, prior_warranty: lookup || null });
  if (!w) {
    return f.serial_number ? (
      <div data-testid="warranty-form-none" className="flex items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-700 dark:text-amber-400 sm:col-span-2">
        <AlertTriangle size={14} /> No outward warranty found for SN <b className="mx-1 font-mono">{f.serial_number}</b>. Enter supply date to check OEM warranty, or confirm coverage with the sender.
      </div>
    ) : null;
  }
  const src = w.source === "outward" ? <>our repair warranty ({w.months}) issued on <b>{w.from}</b>, dispatched {fmtDate(w.start)}</> : <>{w.months} OEM warranty from supply date {fmtDate(w.start)}</>;
  return w.expired ? (
    <div data-testid="warranty-form-alert" className="flex items-start gap-2 rounded-md border border-red-500/40 bg-red-500/10 p-2.5 text-xs text-red-600 dark:text-red-400 sm:col-span-2">
      <AlertTriangle size={16} className="mt-0.5 shrink-0" />
      <div><b>Out of warranty.</b> Based on {src} — ended on <b>{fmtDate(w.expiry)}</b> ({w.daysOver} days ago). Consider booking as <b>New Repair</b> (chargeable) or confirm coverage with the sender.</div>
    </div>
  ) : (
    <div data-testid="warranty-form-ok" className="flex items-center gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-2.5 text-xs text-emerald-700 dark:text-emerald-400 sm:col-span-2">
      <CheckCircle2 size={14} /> <span>In warranty · {src} · valid until <b>{fmtDate(w.expiry)}</b> ({-w.daysOver} days left)</span>
    </div>
  );
}

function useWarrantyLookup(serial, enabled) {
  const [res, setRes] = useState(undefined);
  useEffect(() => {
    if (!enabled || !serial || serial.trim().length < 3) { setRes(undefined); return; }
    setRes("loading");
    const t = setTimeout(() => {
      api.get("/warranty/lookup", { params: { serial: serial.trim() } }).then((r) => setRes(r.data.warranty)).catch(() => setRes(null));
    }, 450);
    return () => clearTimeout(t);
  }, [serial, enabled]);
  return res;
}

function InwardForm({ onClose, onSaved, locations, techs, projects }) {
  const [mode, setMode] = useState("single");
  const [f, setF] = useState({
    serial_number: "", equipment_name: "", model_number: "", brand: "", equipment_category: "",
    origin_location_id: "", project_id: "", assigned_technician_id: "", priority: "normal",
    physical_condition: "", included_accessories: "", sender_contact: "", tracking_rma: "",
    repair_type: "new_repair", warranty_ref: "", supply_date: "", warranty_period_months: 12,
    client_email: "", notify_client: true,
  });
  const [quantity, setQuantity] = useState(10);
  const [serialText, setSerialText] = useState("");
  const [checklist, setChecklist] = useState({});
  const [media, setMedia] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const serials = serialText.split(/\r?\n|,/).map((s) => s.trim()).filter(Boolean);
  const isBatch = mode === "batch";
  const lookup = useWarrantyLookup(f.serial_number, !isBatch && f.repair_type === "warranty_repair");

  const save = async () => {
    if (!f.equipment_name) { toast.error("Equipment name is required"); return; }
    if (!isBatch && !f.serial_number) { toast.error("Serial number is required"); return; }
    if (isBatch && serials.length && serials.length !== Number(quantity)) {
      toast.error(`You pasted ${serials.length} serials but quantity is ${quantity}`); return;
    }
    setBusy(true);
    try {
      const payload = { ...f, condition_checklist: checklist, warranty_period_months: Number(f.warranty_period_months) || 12 };
      ["origin_location_id", "project_id", "assigned_technician_id", "supply_date"].forEach((k) => { if (!payload[k]) payload[k] = null; });
      if (isBatch) {
        const { data } = await api.post("/jobs/batch", { ...payload, quantity: Number(quantity), serial_numbers: serials });
        toast.success(`Batch ${data.batch_number} created · ${data.quantity} job cards`);
        await uploadQueued(media, { entityType: "batch", entityId: data.batch_id, stage: "inward" }, "inward photos");
        const j = data.jobs[0];
        onSaved({
          heading: `Batch Label · ${data.batch_number}`, title: j.equipment_name, code: data.batch_number, badge: typeLabel(j.repair_type),
          lines: [["QTY", `${data.quantity} units`], ["MODEL", j.model_number || "—"], ["BRAND", j.brand || "—"], ["PRIORITY", j.priority]],
        });
      } else {
        const { data } = await api.post("/jobs", payload);
        toast.success(`Inward created: ${data.job_number}`);
        await uploadQueued(media, { entityType: "job", entityId: data.id, stage: "inward" }, "inward photos");
        onSaved(null);
      }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  const tab = (m, label, Icon) => (
    <button type="button" data-testid={`inward-mode-${m}`} onClick={() => setMode(m)}
      className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${mode === m ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
      <Icon size={14} /> {label}
    </button>
  );

  return (
    <Modal open onClose={onClose} title="New Inward Gatepass" wide>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="grid w-full grid-cols-2 gap-1 rounded-md border border-border p-1 sm:inline-flex sm:w-auto">
          {tab("single", "Single Item", PackagePlus)}
          {tab("batch", "Batch × N", Layers)}
        </div>
        <div className="grid w-full grid-cols-2 gap-1 rounded-md border border-border p-1 sm:inline-flex sm:w-auto">
          {REPAIR_TYPES.map((t) => (
            <button key={t.value} type="button" data-testid={`repair-type-${t.value}`} onClick={() => setF({ ...f, repair_type: t.value })}
              className={`rounded-md px-3 py-1.5 text-xs font-bold uppercase tracking-wide ${f.repair_type === t.value ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {isBatch && (
        <div className="mb-3 grid gap-3 rounded-md border border-primary/40 bg-primary/5 p-3 sm:grid-cols-[160px_1fr]">
          <Field label="Quantity" required>
            <Input type="number" min={1} max={1000} data-testid="f-quantity" value={quantity} onChange={(e) => setQuantity(e.target.value)} />
            <p className="mt-1 text-[11px] text-muted-foreground">One job card per unit, all under one Batch ID.</p>
          </Field>
          <Field label={`Serial Numbers (optional · ${serials.length}/${quantity || 0})`}>
            <Textarea rows={3} data-testid="f-serials" value={serialText} onChange={(e) => setSerialText(e.target.value)}
              placeholder={"Paste one serial per line (or comma separated).\nLeave blank to auto-generate BATCH-xxxx-0001 … "} className="font-mono text-xs" />
          </Field>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        {!isBatch && <Field label="Serial Number" required><Input data-testid="f-serial" value={f.serial_number} onChange={set("serial_number")} /></Field>}
        <Field label="Tracking ID / RMA"><Input data-testid="f-rma" value={f.tracking_rma} onChange={set("tracking_rma")} /></Field>
        <Field label="Equipment Name" required><Input data-testid="f-equipment" value={f.equipment_name} onChange={set("equipment_name")} /></Field>
        <Field label="Equipment Category"><Input value={f.equipment_category} onChange={set("equipment_category")} placeholder="RF Module, Display…" /></Field>
        <Field label="Brand"><Input value={f.brand} onChange={set("brand")} /></Field>
        <Field label="Model Number"><Input value={f.model_number} onChange={set("model_number")} /></Field>
        {f.repair_type === "warranty_repair" && (
          <>
            <Field label="Warranty Ref / Invoice No."><Input data-testid="f-warranty-ref" value={f.warranty_ref} onChange={set("warranty_ref")} placeholder="Warranty card / PO / invoice" /></Field>
            <Field label="Supply / Purchase Date (OEM, optional)"><Input type="date" data-testid="f-supply-date" value={f.supply_date} onChange={set("supply_date")} /></Field>
            <Field label="OEM Warranty Period (months)"><Input type="number" min={1} max={120} data-testid="f-warranty-months" value={f.warranty_period_months} onChange={set("warranty_period_months")} /></Field>
            {!isBatch && <WarrantyFormAlert f={f} lookup={lookup} />}
          </>
        )}
        <Field label="Origin Location">
          <Select data-testid="f-origin-location" value={f.origin_location_id} onChange={(e) => {
            const loc = locations.find((l) => l.id === e.target.value);
            setF({ ...f, origin_location_id: e.target.value, client_email: loc?.client_email || f.client_email });
          }}>
            <option value="">—</option>
            {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </Select>
        </Field>
        <Field label="Sender Contact"><Input value={f.sender_contact} onChange={set("sender_contact")} /></Field>
        <Field label="Client Email (for received / dispatched notices)">
          <Input type="email" data-testid="f-client-email" value={f.client_email} onChange={set("client_email")} placeholder="client@company.com" />
        </Field>
        <div className="flex items-end pb-1"><ClientNotify checked={f.notify_client} onChange={(v) => setF({ ...f, notify_client: v })} email={f.client_email} what="received" /></div>
        <Field label="Link to Project">
          <Select value={f.project_id} onChange={set("project_id")}>
            <option value="">Standalone repair</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.project_code} · {p.title}</option>)}
          </Select>
        </Field>
        <Field label="Assign Technician">
          <Select value={f.assigned_technician_id} onChange={set("assigned_technician_id")}>
            <option value="">Unassigned</option>
            {techs.map((t) => <option key={t.id} value={t.id}>{t.full_name}</option>)}
          </Select>
        </Field>
        <Field label="Priority">
          <Select value={f.priority} onChange={set("priority")} data-testid="f-priority">
            {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
          </Select>
        </Field>
        <Field label="Included Accessories"><Input value={f.included_accessories} onChange={set("included_accessories")} placeholder="Cables, remotes, test leads" /></Field>
      </div>

      <div className="mt-3">
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Physical Condition Checklist</div>
        <div className="flex flex-wrap gap-2">
          {CHECKLIST.map((c) => (
            <button key={c} type="button" data-testid={`chk-${c}`} onClick={() => setChecklist({ ...checklist, [c]: !checklist[c] })}
              className={`rounded-md border px-2.5 py-1 text-xs font-medium ${checklist[c] ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:bg-accent"}`}>
              {CHECK_LABELS[c]}
            </button>
          ))}
        </div>
      </div>
      <Field label="Physical Condition Notes"><Textarea className="mt-3" rows={2} value={f.physical_condition} onChange={set("physical_condition")} /></Field>

      <div className="mt-3">
        <div className="mb-1 text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Condition Photos / Video (optional)</div>
        <PendingMedia files={media} onChange={setMedia} />
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <Btn variant="outline" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={busy} data-testid="inward-gatepass-submit-btn">
          {busy ? "Saving…" : isBatch ? `Create Batch (${quantity || 0} cards)` : "Create Gatepass"}
        </Btn>
      </div>
    </Modal>
  );
}
